import { cn } from '~/shared/lib/cn'
import type { PatientClaimFormApi } from '../../../model'
import { DeductionCell } from './deduction-cell'

type DeductionField = 'adjustments' | 'deductible' | 'coInsurance' | 'copayment' | 'nonCovered'

const tdClass =
  'border-divider border-b border-r p-2 focus-within:shadow-[inset_0_0_0_1px_var(--color-primary-main)]'

export function DeductionColumn({
  form,
  index,
  field,
  recordServiceLines,
  onOpenSheet,
  className,
  showAmountTypeToggle,
}: {
  form: PatientClaimFormApi
  index: number
  field: DeductionField
  recordServiceLines: () => void
  onOpenSheet: () => void
  className?: string
  /** When true, renders $/% toggle in the cell. */
  showAmountTypeToggle?: boolean
}) {
  return (
    <td className={cn(tdClass, className)}>
      <form.Field name={`serviceLines[${index}].${field}`}>
        {(deduction) => {
          const val = deduction.state.value ?? {
            amount: null,
            amountType: 'cents' as const,
            codeGroups: [],
          }
          return (
            <DeductionCell
              value={val.amount}
              onCentsChange={(cents) =>
                deduction.handleChange({
                  ...val,
                  amount: cents,
                  // Direct edits override per-code amounts; clear them so future sums
                  // don't silently diverge from the displayed cell value.
                  codeGroups: val.codeGroups.map((g) => ({ ...g, amount: null })),
                })
              }
              onBlur={recordServiceLines}
              codeGroups={val.codeGroups}
              onOpenSheet={onOpenSheet}
              onClearCodes={() => deduction.handleChange({ ...val, codeGroups: [] })}
              alwaysShowAddButton={field === 'adjustments'}
              amountType={showAmountTypeToggle ? (val.amountType ?? 'cents') : undefined}
              onAmountTypeChange={
                showAmountTypeToggle
                  ? (type) => {
                      if (type === (val.amountType ?? 'cents')) return
                      const prev = val.amount
                      // Convert between storage formats keeping display value the same:
                      // cents 5000 → display 50.00 dollars ↔ display 50.00% → ratio 0.5
                      const converted =
                        prev != null ? (type === 'percentage' ? prev / 100 : prev * 100) : prev
                      deduction.handleChange({ ...val, amountType: type, amount: converted })
                      recordServiceLines()
                    }
                  : undefined
              }
            />
          )
        }}
      </form.Field>
    </td>
  )
}
